import { FunctionComponent, useCallback, useEffect, useState } from 'react'
import styled, { css, useTheme } from 'styled-components'
import SvgIcon from '../SvgIcon'
import translate from '@i18n'
import PrimaryCTABlink from '../Buttons/PrimaryCTABlink'
import config from '@config'
import { desktopCSS, mobileCSS } from '@measures/responsive'
import { getDeviceType } from '@utils/deviceType'
import IconButton from '../Buttons/IconButton'
import useFeatureFlagsSkeleton from '@hooks/useFeatureFlagsSkeleton'
import QrcodeBlick from '@assets/images/qrcode-blick-app-de.svg'
import QrcodeFr from '@assets/images/qrcode-blick-app-fr.svg'
import { getLocalStorageItem, setLocalStorageItem } from '@utils/localStorage'

const {
  publication: { publication },
  appLinks: { ios, android },
  fmtOnboardingContainer: {
    localStorageHideFmtOnboardingContainerKeySelectTeamList,
    localStorageHideFmtOnboardingContainerKeyTeamsTab,
    moreInfoLink,
  },
} = config

type FmTOnboardingContainerProps = {
  position: 'selectTeam' | 'sportCompetitorsList'
}

const StyledWrapper = styled.div<FmTOnboardingContainerProps>`
  ${({
    theme: {
      colors: { fillSport, staticBlack },
      spacing: { spacing16, spacing8 },
    },
    position,
  }) => css`
    border-radius: ${spacing8};
    padding: ${position === 'selectTeam'
      ? `${spacing8} ${spacing8} ${spacing16} ${spacing8}`
      : spacing16};
    background: linear-gradient(85deg, ${staticBlack} -68%, ${fillSport} 68%);
    display: grid;
    gap: ${spacing8};
    grid-template-columns: auto 1fr auto;
  `}
`

const StyledBlickIcon = styled(SvgIcon)`
  ${({
    theme: {
      spacing: { spacing8, spacing40 },
    },
  }) => css`
    min-width: ${spacing40};
    border-radius: ${spacing8};
  `}
`
const StyledMoreInfoButton = styled(
  PrimaryCTABlink
)<FmTOnboardingContainerProps>`
  ${({
    theme: {
      spacing: { spacing40 },
      colors: { staticWhite },
    },
    position,
  }) => css`
    grid-area: 2 / 1 / 3 / 2;
    margin-left: ${position === 'selectTeam' ? '0' : spacing40};

    ${mobileCSS(css`
      background-color: transparent;
      border: 1px solid ${staticWhite};
      color: ${staticWhite};
    `)}
  `}
`

const StyledDownloadAppButton = styled(PrimaryCTABlink)`
  display: none;
  grid-area: 2 / 2 / 3 / 4;
  ${mobileCSS(css`
    display: flex;
    text-align: center;
  `)};
`

const StyledQRCode = styled.img<FmTOnboardingContainerProps>`
  ${({
    theme: {
      spacing: { spacing80 },
    },
    position,
  }) => css`
    height: ${spacing80};
    align-self: center;
    grid-area: ${position === 'selectTeam' ? '2 / 2 / 3 / 4' : '1 / 2 / 3 / 3'};
    justify-self: end;
    display: block;
    ${mobileCSS(css`
      display: none;
    `)}
  `}
`

const StyledBlickLogoAndTitleWrapper = styled.div<FmTOnboardingContainerProps>`
  ${({
    theme: {
      spacing: { spacing8 },
    },
    position,
  }) => css`
    display: flex;
    gap: ${spacing8};
    align-items: center;
    grid-area: ${position === 'selectTeam' ? '1 / 1 / 2 / 3' : '1 / 1 / 2 / 2'};

    ${mobileCSS(css`
      grid-area: 1 / 1 / 2 / 3;
    `)}
  `}
`

const StyledCloseButton = styled(IconButton)<FmTOnboardingContainerProps>`
  ${({
    theme: {
      spacing: { spacing24 },
    },
    position,
  }) => css`
    width: ${spacing24};
    height: ${spacing24};
    grid-area: 1 / 3 / 2 / 4;
    ${mobileCSS(css`
      ${position === 'selectTeam' && 'display: none;'}
    `)}
  `}
`

const StyledContainerTitle = styled.div`
  ${({
    theme: {
      typography: {
        headings: {
          xsmall: { bundledCSS: xsmallHeadingCSS },
          xxsmall: { bundledCSS: xxsmallHeadingCSS },
        },
      },
      colors: { staticWhite },
    },
  }) => css`
    white-space: pre-wrap;
    ${xxsmallHeadingCSS};
    color: ${staticWhite};
    ${desktopCSS(css`
      ${xsmallHeadingCSS};
    `)}
  `}
`

const FmTOnboardingContainer: FunctionComponent<
  FmTOnboardingContainerProps
> = ({ position }) => {
  const theme = useTheme()
  const isRomandie = publication === 'romandie'
  const isIos = getDeviceType() === 'ios'
  const { isFollowMyTeamEnabled } = useFeatureFlagsSkeleton()
  const [showFmtOnboardingContainer, setShowFmtOnboardingContainer] =
    useState<boolean>(true)
  const [appLink, setAppLink] = useState('')

  useEffect(() => {
    setAppLink(isIos ? ios : android)
  }, [isIos])

  useEffect(() => {
    const hasBeenDismissed = getLocalStorageItem(
      position === 'selectTeam'
        ? localStorageHideFmtOnboardingContainerKeySelectTeamList
        : localStorageHideFmtOnboardingContainerKeyTeamsTab
    )

    setShowFmtOnboardingContainer(!hasBeenDismissed)
  }, [position])

  const onClose = useCallback(() => {
    const localStorageHideFmtOnboardingContainerKey =
      position === 'selectTeam'
        ? localStorageHideFmtOnboardingContainerKeySelectTeamList
        : localStorageHideFmtOnboardingContainerKeyTeamsTab

    setShowFmtOnboardingContainer(false)
    setLocalStorageItem(localStorageHideFmtOnboardingContainerKey, position)
  }, [position])

  return showFmtOnboardingContainer && isFollowMyTeamEnabled ? (
    <StyledWrapper position={position}>
      <StyledBlickLogoAndTitleWrapper position={position}>
        <StyledBlickIcon iconName="fmt-blick-logo" size={40} />
        <StyledContainerTitle>
          {translate('fmtOnboardingContainer.title')}
        </StyledContainerTitle>
      </StyledBlickLogoAndTitleWrapper>
      <StyledMoreInfoButton
        position={position}
        colorVariant="white"
        size="small"
        href={moreInfoLink}
        target="_blank">
        {translate('fmtOnboardingContainer.button.moreInfo')}
      </StyledMoreInfoButton>
      <StyledDownloadAppButton
        colorVariant="white"
        size="small"
        href={appLink}
        target="_blank"
        rel="nofollow noopener">
        {translate('fmtOnboardingContainer.button.downloadApp')}
      </StyledDownloadAppButton>
      <StyledQRCode
        position={position}
        alt="appstore code"
        src={isRomandie ? QrcodeFr : QrcodeBlick}
      />
      <StyledCloseButton
        position={position}
        type="button"
        iconName="xmark-large"
        iconSize={24}
        iconColor={theme.colors.staticWhite}
        ariaLabel="close"
        onClick={onClose}
      />
    </StyledWrapper>
  ) : null
}

export default FmTOnboardingContainer
